import Table from "@commons/Table/TableContent";
import { useGetUsers } from "@services/users/list";
import { toast } from "react-toastify";
import { useState, useEffect, useMemo } from "react";
import { UsersColumns } from "@typings/entities/users/table";
import type { UserItemList } from "@services/users/list/type";
import { config } from "@/config";
import ConfirmationModal from "@commons/Modal/ConfirmationModal";
import { blockUser } from "@services/users/block";
import { deleteUser } from "@services/users/delete";
import { AppError } from "@utils/errors/appError";
import { USER_STATUS } from "@typings/entities/users/enums";
import { formatDateTime } from "@utils/formatters";

type TableActiveUsersProps = {
  search: string;
  onEditUser: (user: UserItemList) => void;
  onSuccess: () => void;
};

export default function TableActiveUsers({
  search,
  onEditUser,
  onSuccess,
}: TableActiveUsersProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(config.table.defaultPageSize);
  const [userToDelete, setUserToDelete] = useState<UserItemList | null>(null);
  const [userToBlock, setUserToBlock] = useState<UserItemList | null>(null);

  const { data, error, isLoading } = useGetUsers({
    page: page + 1,
    limit: rowsPerPage,
    search: search || undefined,
    status: USER_STATUS.Enabled,
  });

  const formattedData = useMemo(() => {
    if (!data) return data;
    return {
      ...data,
      items: data.items.map((user) => ({
        ...user,
        lastLogin: formatDateTime(user.lastLogin),
      })),
    };
  }, [data]);

  const heads = UsersColumns;

  useEffect(() => {
    setPage(0);
  }, [search]);

  const handleEdit = (user: UserItemList) => {
    onEditUser(user);
  };

  const handleDelete = (user: UserItemList) => {
    setUserToDelete(user);
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    try {
      await deleteUser(userToDelete.id);
      toast.success(`Usuário ${userToDelete.username} deletado com sucesso`);
      onSuccess();
      setUserToDelete(null);
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao deletar usuário";
      toast.error(title);
    }
  };

  const handleBlock = (user: UserItemList) => {
    setUserToBlock(user);
  };

  const handleConfirmBlock = async () => {
    if (!userToBlock) return;
    try {
      await blockUser(userToBlock.id);
      toast.success(`Usuário ${userToBlock.username} bloqueado com sucesso`);
      onSuccess();
      setUserToBlock(null);
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao bloquear usuário";
      toast.error(title);
    }
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (error) {
      toast.error("Erro ao carregar os usuários ativos");
    }
  }, [error]);

  return (
    <div className="w-full">
      <div className="w-full flex flex-col justify-center items-center text-center bg-white rounded-lg shadow-md p-4">
        <Table.Root isLoading={isLoading}>
          <Table.Head heads={heads} actions />
          <Table.Body
            rows={formattedData?.items ?? []}
            columns={heads.map((head) => head.id)}
            actions={["edit", "delete", "block"]}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onBlock={handleBlock}
          />
          <Table.Footer
            colSpan={heads.length}
            count={formattedData?.meta?.totalItems ?? 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </Table.Root>
      </div>

      <ConfirmationModal
        open={!!userToDelete}
        title="Deletar Usuário"
        message={`Tem certeza que deseja deletar o usuário "${userToDelete?.username}"? Esta ação não pode ser desfeita.`}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleConfirmDelete}
        confirmLabel="Deletar"
        variant="danger"
      />

      <ConfirmationModal
        open={!!userToBlock}
        title="Bloquear Usuário"
        message={`Tem certeza que deseja bloquear o usuário "${userToBlock?.username}"? O usuário não poderá mais acessar o sistema.`}
        onClose={() => setUserToBlock(null)}
        onConfirm={handleConfirmBlock}
        confirmLabel="Bloquear"
        variant="warning"
      />
    </div>
  );
}
